/* eslint-disable prettier/prettier */
import { apiService } from './api.service';
import { ApiResponse } from '../types';

// ---- Types ----

export interface CreateEnrollmentPayload {
	courseid: string;
	userid: string;
	enrolledby?: string;
	expiresat?: string;
	metadata?: any;
}

export interface UpdateEnrollmentPayload {
	status?: 'active' | 'completed' | 'dropped' | 'suspended';
	progress?: number;
	lastaccessed?: string;
	completedat?: string;
	certificateurl?: string;
	metadata?: any;
}

export interface BulkEnrollPayload {
	courseid: string;
	userids: string[];
	enrolledby?: string;
	expiresat?: string;
	metadata?: any;
}

// ---- Service ----

export const courseEnrollmentService = {
	// ✅ Create Enrollment
	createEnrollment: async (
		payload: CreateEnrollmentPayload
	): Promise<ApiResponse<any>> => {
		try {
			const res = await apiService.post<ApiResponse<any>>(
				'/enrollments/enrollments',
				payload
			);

			const response = (res as any).data ?? res;

			if (!response.success) {
				throw new Error(
					response.message || 'Failed to create enrollment'
				);
			}

			return response;
		} catch (error: any) {
			console.error('createEnrollment error:', error);

			return {
				success: false,
				status: 500, // ✅ FIX
				message: error.message || 'Failed to create enrollment',
				data: null,
			};
		}
	},

	async getEnrollmentById(id: string) {
		try {
			return await apiService.get<any>(`/enrollments/${id}`);
		} catch (error: any) {
			throw error?.response?.data || error;
		}
	},

	async getUserEnrollments(status?: string) {
		try {
			return await apiService.get<any[]>('/users/me/enrollments', {
				status,
			});
		} catch (error: any) {
			throw error?.response?.data || error;
		}
	},

	async getCourseEnrollments(courseId: string) {
		try {
			return await apiService.get<any[]>(
				`/enrollments/courses/${courseId}/enrollments`
			);
		} catch (error: any) {
			throw error?.response?.data || error;
		}
	},

	async updateEnrollment(id: string, payload: UpdateEnrollmentPayload) {
		try {
			return await apiService.put<any>(`/enrollments/${id}`, payload);
		} catch (error: any) {
			throw error?.response?.data || error;
		}
	},

	async deleteEnrollment(enrollmentId: string): Promise<void> {
		try {
			const response = await apiService.delete<void>(
				`enrollments/enrollments/${enrollmentId}`
			);

			if (!response.success) {
				throw new Error(
					response.message || 'Failed to delete enrollment'
				);
			}

			return response.data;
		} catch (error) {
			console.error('Error in deleteEnrollment:', error);
			throw error;
		}
	},

	async getEnrollmentStats(courseId: string) {
		try {
			return await apiService.get<any>(
				`/course/${courseId}/enrollments/stats`
			);
		} catch (error: any) {
			throw error?.response?.data || error;
		}
	},

	async bulkEnroll(payload: BulkEnrollPayload) {
		try {
			return await apiService.post<any>('/enrollments/bulk', payload);
		} catch (error: any) {
			throw error?.response?.data || error;
		}
	},
};
